/**
 * Agent WebSocket — unified bot WS for live updates
 *
 * Connects to the appropriate WebSocket endpoint on bot review pages
 * and dispatches incoming messages to per-type handlers. Manages
 * connection lifecycle across SPA navigations.
 */

import { playAttentionSound as playSound, unlockAudio as unlockSound } from './attention-sound.js';

(function () {

    // --- Endpoint registry ---------------------------------------------------
    // Each entry maps a DOM marker (present when a case sheet is open) to a
    // WebSocket endpoint. Marker-based detection works on both dedicated review
    // pages and inline sheets opened over list pages (e.g. propmgmt rentals).

    // Marker matches a case sheet OR a dashboard/activity-log page for that bot.
    var endpoints = [
        { marker: '[data-case-dq-id], [data-bot-page="collections"]', ws: '/ws/collections/', name: 'collections' },
        { marker: '[data-case-lead-id], [data-bot-page="leasing"]', ws: '/ws/leasing/', name: 'leasing' },
    ];

    function getActiveEndpoint() {
        for (var i = 0; i < endpoints.length; i++) {
            if (document.querySelector(endpoints[i].marker)) {
                return endpoints[i];
            }
        }
        return null;
    }

    // --- Row refresh ---------------------------------------------------------

    var lastUnreadCounts = null;

    // Selector attribute per domain — the chat-icon `<i>` carries the id.
    // Leasing rows use `data-lead-sms="{lead.pk}"`, delinquency rows use `data-dq-sms="{dq.pk}"`.
    function unreadAttrForActive() {
        if (activeEndpoint && activeEndpoint.name === 'collections') return 'data-dq-sms';
        return 'data-lead-sms';
    }

    function updateUnreadBadges(counts) {
        lastUnreadCounts = counts;
        var attr = unreadAttrForActive();
        var icons = document.querySelectorAll('[' + attr + ']');
        for (var i = 0; i < icons.length; i++) {
            var btn = icons[i].closest('.btn-icon') || icons[i];
            // Only manage the unread-MESSAGE dot here; leave the bot-decision
            // review badge ([data-inquiry-review-badge]) intact — it's a separate
            // server-rendered indicator and must not be wiped on unread refresh.
            var existing = btn.querySelector('.unread-dot:not([data-inquiry-review-badge])');
            if (existing) existing.remove();
        }
        for (var caseId in counts) {
            if (counts[caseId] > 0) {
                // A lead can appear on multiple rows (several inquiries/tours) — paint every
                // matching chat icon, not just the first, so it agrees with the server-rendered dot.
                var matches = document.querySelectorAll('[' + attr + '="' + caseId + '"]');
                for (var m = 0; m < matches.length; m++) {
                    var dotBtn = matches[m].closest('.btn-icon') || matches[m];
                    var dot = document.createElement('span');
                    dot.className = 'unread-dot';
                    dotBtn.appendChild(dot);
                }
            }
        }
    }

    function fetchInitialUnreadCounts(url) {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    var data = JSON.parse(xhr.responseText);
                    if (data.counts) updateUnreadBadges(data.counts);
                } catch (e) {}
            }
        };
        xhr.send();
    }

    function refreshRow(logId) {
        var tbody = document.getElementById('entry-' + logId);
        if (!tbody) return;
        // The row stamps its own refresh URL with its layout variant baked in
        // (_entry_row.html): the By-Tenant grouping renders 6 columns, the flat and
        // by-date tables 8. A bare path would inject two extra <td>s into the narrow one.
        var url = tbody.getAttribute('data-entry-refresh-url')
            || '/chatbots/collections/review/log/' + logId + '/row/';
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                var parent = tbody.parentNode;
                if (!parent) return;
                tbody.outerHTML = xhr.responseText;
                if (window.htmx) htmx.process(parent);
            }
        };
        xhr.send();
    }

    // Refresh a list-page row by id (leasing or delinquency).
    // - On 2xx with body: swap the row in place; re-run updateUnreadBadges.
    // - On 204 (or empty body): row no longer belongs on this tab — remove it.
    // Forwards the active list tab as ?tab=<name> when a [data-dq-list-tab] marker is present
    // so the row partial can decide whether the dq still matches.
    function refreshListRow(elementId, urlPath) {
        var row = document.getElementById(elementId);
        if (!row) return;
        var tabMarker = document.querySelector('[data-dq-list-tab]');
        if (tabMarker) {
            var tab = tabMarker.getAttribute('data-dq-list-tab');
            if (tab) urlPath += (urlPath.indexOf('?') >= 0 ? '&' : '?') + 'tab=' + encodeURIComponent(tab);
        }
        var xhr = new XMLHttpRequest();
        xhr.open('GET', urlPath);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300) return;
            var live = document.getElementById(elementId);
            if (!live) return;
            var body = xhr.responseText.trim();
            if (xhr.status === 204 || !body) {
                live.parentNode && live.parentNode.removeChild(live);
                return;
            }
            var parent = live.parentNode;
            live.outerHTML = body;
            if (window.htmx && parent) htmx.process(parent);
            if (lastUnreadCounts) updateUnreadBadges(lastUnreadCounts);
        };
        xhr.send();
    }

    // Re-fetch a leasing Activity card after a phone call is logged to it
    // (PropmgmtLog). Mirrors acqs-ws.js::refreshCallActivityCard — find the
    // on-screen card for this lead (the lead-detail card OR the inquiry-detail
    // card, both tagged data-lead-id) and swap in the freshly rendered fragment
    // so the [CALL] entry appears without a reload. No-op when no such card is on
    // the page (list pages, chat sheet), so the event is inert except on the
    // lead/inquiry detail surfaces.
    function refreshCallActivityCard(leadId) {
        if (leadId == null) return;
        var card = document.querySelector('[data-call-activity][data-lead-id="' + leadId + '"]');
        if (!card) return;
        var url = card.getAttribute('data-refresh-url');
        if (!url) return;
        var cardId = card.id;
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.setRequestHeader('HX-Request', 'true');
        xhr.onload = function () {
            if (xhr.status < 200 || xhr.status >= 300) return;
            var live = document.getElementById(cardId);
            if (!live) return;
            var parent = live.parentNode;
            live.outerHTML = xhr.responseText;
            if (window.htmx && parent) htmx.process(parent);
        };
        xhr.send();
    }

    // --- Leasing attention module --------------------------------------------
    // Polls /propmgmt/inquiries/attention/ for live counts + draft/escalation ids.
    // Updates tab badges and toasts (+ sound when backgrounded) on new items.
    // Starts when the [data-leasing-list-tab] marker is in the DOM; resets on
    // SPA-nav away. Also called from the case_updated WS handler for instant updates.

    var lastCounts = null;
    var seenDraftIds = new Set();
    var seenEscalatedLeadIds = new Set();
    var pollTimer = null;

    function getAttentionMarker() {
        return document.querySelector('[data-leasing-list-tab]');
    }

    function updateAttentionBadges(data) {
        // Drive every tab badge present in the DOM from the matching count key — covers the
        // cross-cutting tabs (open / in_pipeline / needs_review / escalated / assigned_to_human)
        // AND the per-stage tabs (new, initial_outreach, …), all of which the attention endpoint
        // returns. Iterating the DOM (not a hardcoded list) means new stages/tabs auto-update.
        var badges = document.querySelectorAll('[data-attention-badge]');
        for (var b = 0; b < badges.length; b++) {
            var key = badges[b].getAttribute('data-attention-badge');
            // Only numeric count keys — skips absent keys AND non-count payload like the
            // draft_ids / escalated_lead_ids arrays (which are truthy and would stringify).
            if (typeof data[key] !== 'number') continue;
            var count = data[key];
            var prefix = badges[b].getAttribute('data-badge-prefix') || '';  // e.g. "+" for booked-today
            badges[b].textContent = prefix + count;
            if (count) {
                badges[b].classList.remove('d-none');
            } else {
                badges[b].classList.add('d-none');
            }
        }
    }

    // The sound lives in attention-sound.js, shared with the General text toast (sms-envelope.js).
    function attentionSoundUrl() {
        var marker = getAttentionMarker();
        return marker && marker.getAttribute('data-attention-sound');
    }

    function playAttentionSound() {
        playSound(attentionSoundUrl());
    }

    function unlockAudio() {
        unlockSound(attentionSoundUrl());
    }

    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });

    function notifyNewAttention(count, kind) {
        if (window.SeeMe && SeeMe.toast) {
            var escalated = kind === 'escalated';
            var msg = escalated
                ? (count > 1 ? count + ' new escalations' : 'New escalation')
                : (count > 1 ? count + ' new items to review' : 'New item to review');
            SeeMe.toast(msg, {
                type: escalated ? 'error' : 'warning',  // escalation = danger, draft = warning
                position: 'top-right',
                duration: 8000,
            });
        }
        // Always alert — sound plays whether the tab is focused or backgrounded.
        playAttentionSound();
    }

    // New-inquiry FYI toast — coalesces a burst (e.g. two inquiries created back-to-back in
    // one import run) into ONE green toast + ONE ping. Debounced ~2s: the count accumulates,
    // a single trailing timer fires the toast. Bounded to <=1 toast/ping per window.
    var newInquiryCount = 0;
    var newInquiryTimer = null;
    function flashNewInquiry() {
        newInquiryCount++;
        if (newInquiryTimer) return;
        newInquiryTimer = setTimeout(function () {
            var n = newInquiryCount;
            newInquiryCount = 0;
            newInquiryTimer = null;
            if (window.SeeMe && SeeMe.toast) {
                var msg = n > 1 ? (n + ' new inquiries') : 'New inquiry!';
                SeeMe.toast(msg, { type: 'success', position: 'top-right', duration: 8000 });
            }
            playAttentionSound();
        }, 2000);
    }

    // Tour booked/confirmed alert — a STICKY, CLICKABLE blue notification at the list level (fires
    // whether or not the lead's sheet is open). These auto-send with no draft to review, so the
    // notification IS the awareness: it stays until dismissed (✕), and clicking it opens the lead's
    // case sheet. A distinct synthesized two-note chime (not the attention.wav ping) sets them apart.
    var pendingTourEvents = [];
    var pendingTourKeys = {};
    var tourEventTimer = null;
    function flashTourEvent(toast, leadId) {
        if (!toast) return;
        var key = (leadId || '') + '|' + toast;
        if (pendingTourKeys[key]) return;  // de-dupe a double-broadcast of the same event
        pendingTourKeys[key] = true;
        pendingTourEvents.push({ toast: toast, leadId: leadId });
        if (tourEventTimer) return;
        tourEventTimer = setTimeout(function () {
            var events = pendingTourEvents;
            pendingTourEvents = [];
            pendingTourKeys = {};
            tourEventTimer = null;
            events.forEach(function (ev) { showTourNotification(ev.toast, ev.leadId); });
            playTourChime();
        }, 800);
    }

    function showTourNotification(toast, leadId) {
        if (!(window.SeeMe && SeeMe.toast)) return;
        var parts = String(toast).split('\n');
        var title = parts[0] || '';
        var detail = parts.slice(1).join(' ');
        // Sticky (duration:0) + blue (info). SeeMe.toast returns the element so we can decorate it.
        var el = SeeMe.toast(title, { type: 'info', position: 'top-right', duration: 0 });
        if (!el) return;
        el.style.alignItems = 'center';
        // Mac-style title/subtitle: bold event+name on line 1, muted address · time on line 2.
        var span = el.querySelector('span');
        if (span) {
            span.textContent = '';
            var titleEl = document.createElement('div');
            titleEl.textContent = title;
            titleEl.style.fontWeight = '600';
            span.appendChild(titleEl);
            if (detail) {
                var detailEl = document.createElement('div');
                detailEl.textContent = detail;
                detailEl.style.cssText = 'font-size:.92em;opacity:.8;margin-top:3px;';
                span.appendChild(detailEl);
            }
        }
        if (leadId) {
            el.style.cursor = 'pointer';
            el.title = 'Open case';
            el.addEventListener('click', function (e) {
                if (e.target.closest('[data-tour-toast-close]')) return;  // ✕ handles its own click
                var reviewUrl = '/chatbots/leasing/review/' + leadId + '/';
                if (window.rfSheet && window.rfSheet.open) {
                    // Open the case sheet IN-PLACE (no reload) so the OTHER notifications survive;
                    // dismiss just this one, since it's been actioned.
                    window.rfSheet.open(reviewUrl);
                    el.classList.add('toast--leaving');
                    el.addEventListener('animationend', function () { if (el.parentNode) el.parentNode.removeChild(el); });
                } else {
                    // Fallback (e.g. not on a leasing page): full navigation with the ?sheet= param.
                    window.location.href = '/propmgmt/rentals/inquiries/needs_review/?sheet=' + reviewUrl;
                }
            });
        }
        // ✕ dismiss — sticky toasts need a manual close.
        var close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('data-tour-toast-close', '');
        close.setAttribute('aria-label', 'Dismiss');
        close.innerHTML = '&times;';
        close.style.cssText = 'margin-left:12px;background:none;border:0;color:inherit;font-size:18px;' +
            'line-height:1;cursor:pointer;opacity:.6;padding:0 2px;';
        close.addEventListener('click', function (e) {
            e.stopPropagation();
            el.classList.add('toast--leaving');
            el.addEventListener('animationend', function () { if (el.parentNode) el.parentNode.removeChild(el); });
        });
        el.appendChild(close);
    }

    // Distinct two-note "ding" via Web Audio — no audio file, clearly different from attention.wav.
    var tourAudioCtx = null;
    function playTourChime() {
        try {
            var AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            if (!tourAudioCtx) tourAudioCtx = new AC();
            var ctx = tourAudioCtx;
            if (ctx.state === 'suspended') ctx.resume();
            var now = ctx.currentTime;
            [[783.99, 0], [1046.50, 0.13]].forEach(function (pair) {  // G5 -> C6, gentle ascending
                var osc = ctx.createOscillator();
                var gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = pair[0];
                var t = now + pair[1];
                gain.gain.setValueAtTime(0.0001, t);
                gain.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(t);
                osc.stop(t + 0.4);
            });
        } catch (e) { /* no-op */ }
    }

    function refreshAttention() {
        var marker = getAttentionMarker();
        if (!marker) return;
        var url = marker.getAttribute('data-attention-url');
        if (!url) return;
        fetch(url + window.location.search, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function (r) { if (!r.ok) throw new Error('attention fetch ' + r.status); return r.json(); })
            .then(function (data) {
                updateAttentionBadges(data);

                var draftIds = data.draft_ids || [];
                var escalatedIds = data.escalated_lead_ids || [];

                if (lastCounts === null) {
                    // First load — seed Sets silently (no toast for items already on screen)
                    for (var i = 0; i < draftIds.length; i++) seenDraftIds.add(draftIds[i]);
                    for (var j = 0; j < escalatedIds.length; j++) seenEscalatedLeadIds.add(escalatedIds[j]);
                } else {
                    // Diff — toast on genuinely new ids
                    var newDrafts = [];
                    for (var d = 0; d < draftIds.length; d++) {
                        if (!seenDraftIds.has(draftIds[d])) newDrafts.push(draftIds[d]);
                    }
                    var newEscalated = [];
                    for (var e = 0; e < escalatedIds.length; e++) {
                        if (!seenEscalatedLeadIds.has(escalatedIds[e])) newEscalated.push(escalatedIds[e]);
                    }
                    if (newEscalated.length) {
                        notifyNewAttention(newEscalated.length, 'escalated');
                        for (var ei = 0; ei < newEscalated.length; ei++) seenEscalatedLeadIds.add(newEscalated[ei]);
                    }
                    if (newDrafts.length) {
                        notifyNewAttention(newDrafts.length, 'draft');
                        for (var di = 0; di < newDrafts.length; di++) seenDraftIds.add(newDrafts[di]);
                    }
                }

                lastCounts = data;
            })
            .catch(function () {});
    }

    function startAttentionPoll() {
        if (pollTimer) return;
        refreshAttention();
        pollTimer = setInterval(function () {
            if (!document.hidden) refreshAttention();
        }, 60000);
    }

    function stopAttentionPoll() {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        if (newInquiryTimer) { clearTimeout(newInquiryTimer); newInquiryTimer = null; }
        newInquiryCount = 0;
        lastCounts = null;
        seenDraftIds = new Set();
        seenEscalatedLeadIds = new Set();
    }

    document.addEventListener('visibilitychange', function () {
        if (!document.hidden && getAttentionMarker()) {
            refreshAttention();
        }
    });

    // Refresh every inquiry-list row belonging to a lead (the Needs-Review badge
    // cell lives in the row). Fired on case_updated — a resolved draft clears the
    // badge, and on the Needs Review tab the row endpoint 204s so the row drops.
    // Forwards the current list tab via [data-leasing-list-tab].
    function refreshInquiryRowsForLead(leadId) {
        var rows = document.querySelectorAll('[data-inquiry-lead="' + leadId + '"]');
        if (!rows.length) return;
        var tabMarker = document.querySelector('[data-leasing-list-tab]');
        var tab = tabMarker ? tabMarker.getAttribute('data-leasing-list-tab') : '';
        rows.forEach(function (row) {
            var id = row.id;
            if (!id) return;
            var pk = id.replace('leasing-inquiry-', '');
            var urlPath = '/propmgmt/inquiries/' + pk + '/row/';
            if (tab) urlPath += '?tab=' + encodeURIComponent(tab);
            refreshListRow(id, urlPath);
        });
    }

    // Refresh every activity-log entry row belonging to a lead (the flag chip lives in
    // the row). Fired on case_updated — resolving a flag from a case sheet opened over
    // the review list has to update the list underneath it. Each row carries its own
    // refresh URL with its layout variant baked in, so this works from any of the table
    // shapes (5/7/8 columns) without the client knowing which one it's looking at.
    function refreshEntryRowsForLead(leadId) {
        var rows = document.querySelectorAll('[data-entry-lead="' + leadId + '"]');
        for (var i = 0; i < rows.length; i++) {
            var url = rows[i].getAttribute('data-entry-refresh-url');
            if (rows[i].id && url) refreshListRow(rows[i].id, url);
        }
    }

    // --- Auto-approve countdown ticker ---------------------------------------
    // Every second, refresh the label of every [data-auto-approve-at] element on
    // the page (timed-draft review-card footers + inquiry-list rows). The attribute
    // holds an ISO timestamp; we show "Auto-sends in M:SS" (or "H:MM:SS" once an hour
    // out), and "Auto-sending…" at/under zero. We re-query each tick (never cache the
    // node list) so elements swapped in later by HTMX or the WS row refresh are covered.

    function formatCountdown(ms) {
        var totalSec = Math.floor(ms / 1000);
        var h = Math.floor(totalSec / 3600);
        var m = Math.floor((totalSec % 3600) / 60);
        var s = totalSec % 60;
        var two = function (n) { return n < 10 ? '0' + n : '' + n; };
        if (h >= 1) return h + ':' + two(m) + ':' + two(s);
        return m + ':' + two(s);
    }

    function tickAutoApprove() {
        var els = document.querySelectorAll('[data-auto-approve-at]');
        if (!els.length) return;
        var now = Date.now();
        for (var i = 0; i < els.length; i++) {
            var el = els[i];
            var iso = el.getAttribute('data-auto-approve-at');
            var target = Date.parse(iso);
            if (isNaN(target)) continue;
            var remaining = target - now;
            // Compact (list rows): clock icon + digits only ("25:41"). Full (review card):
            // "Auto-sends in M:SS". Either way we preserve the leading <i> icon and rewrite
            // only the trailing text node, so the FA glyph survives each tick.
            var compact = el.hasAttribute('data-countdown-compact');
            var time = formatCountdown(remaining);
            var label = remaining <= 0
                ? (compact ? '0:00' : 'Auto-sending…')
                : (compact ? time : 'Auto-sends in ' + time);
            var textNode = el.lastChild;
            if (textNode && textNode.nodeType === 3) {
                textNode.textContent = ' ' + label;
            } else {
                el.appendChild(document.createTextNode(' ' + label));
            }
        }
    }

    setInterval(tickAutoApprove, 1000);
    tickAutoApprove();

    // --- Processing state ----------------------------------------------------

    var dotsTimer = null;
    var processingDqId = null;  // tracks which case is mid-processing (survives sheet close/reopen)

    function startDots() {
        stopDots();
        var dots = document.getElementById('bot-working-dots');
        if (!dots) return;
        dotsTimer = setInterval(function () {
            dots.textContent = '.'.repeat((dots.textContent.length % 3) + 1);
        }, 400);
    }

    function stopDots() {
        if (dotsTimer) { clearInterval(dotsTimer); dotsTimer = null; }
    }

    function setCaseProcessing(active, dqId) {
        if (active && dqId) processingDqId = dqId;
        if (!active) processingDqId = null;
        var footer = document.getElementById('case-footer');
        if (footer) {
            if (active) {
                footer.classList.add('is-processing');
            } else {
                footer.classList.remove('is-processing');
            }
            // Disable/enable all inputs in footer
            footer.querySelectorAll('textarea, button, input').forEach(function(el) {
                el.disabled = active;
            });
        }
        var badge = document.getElementById('bot-processing-badge');
        if (active && !badge) {
            var toolbar = document.querySelector('[data-sheet-toolbar]');
            if (toolbar) {
                var span = document.createElement('span');
                span.className = 'data-table__status data-table__status--warning';
                span.id = 'bot-processing-badge';
                span.textContent = 'working\u2026';
                toolbar.appendChild(span);
            }
        } else if (!active && badge) {
            badge.remove();
        }
        // Animated dots
        if (active) { startDots(); } else { stopDots(); }
    }

    // --- WebSocket connection ------------------------------------------------

    var socket = null;
    var activeEndpoint = null;
    var reconnectTimer = null;
    var reconnectAttempts = 0;
    var sentMarkAsReadIds = new Set();

    // Send `mark_as_read` for any visible inbound message bubbles we haven't
    // sent yet this session. Server side: agent/consumers.py BotWSConsumer.receive
    // -> mark_messages_as_read.
    function collectAndSendMarkAsRead() {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;

        var bubbles = document.querySelectorAll('[data-msg-direction="in"][data-msg-id]');
        var ids = [];
        bubbles.forEach(function (el) {
            var id = parseInt(el.dataset.msgId, 10);
            if (id && !sentMarkAsReadIds.has(id)) {
                ids.push(id);
                sentMarkAsReadIds.add(id);
            }
        });

        if (ids.length === 0) return;

        socket.send(JSON.stringify({
            type: 'mark_as_read',
            message_ids: ids,
        }));
    }

    function connect(endpoint) {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }

        activeEndpoint = endpoint;
        var protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        socket = new WebSocket(protocol + '//' + location.host + endpoint.ws);

        socket.onopen = function () {
            reconnectAttempts = 0;
            if (endpoint.name === 'leasing') {
                fetchInitialUnreadCounts('/api/leasing-bot/unprocessed-counts/');
            } else if (endpoint.name === 'collections') {
                fetchInitialUnreadCounts('/api/rent-collections/unprocessed-counts/');
            }
            // If a sheet timeline is already in the DOM (e.g. WS reconnect over
            // an open sheet), mark its visible inbound messages as read.
            collectAndSendMarkAsRead();
        };

        socket.onmessage = function (e) {
            var data = JSON.parse(e.data);
            var msg = data.update;
            if (!msg) return;

            if (msg.type === 'action_log_updated') {
                refreshRow(msg.log_id);
            }

            if (msg.message_status_update) {
                var patched = false;
                if (msg.msg_id) {
                    var statusEl = document.querySelector('[data-msg-status="' + msg.msg_id + '"]');
                    var bubble = document.querySelector('[data-msg-id="' + msg.msg_id + '"]');
                    var newStatus = msg.msg_status;
                    if (bubble) {
                        var meta = bubble.querySelector('.chat-log__meta');
                        if (newStatus === 'failed' || newStatus === 'undelivered' || newStatus === 'canceled') {
                            var label = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
                            var html = '<i class="fa-solid fa-triangle-exclamation"></i> ' + label;
                            if (statusEl) {
                                statusEl.className = 'chat-log__status text-error fs-xs';
                                statusEl.setAttribute('title', newStatus);
                                statusEl.innerHTML = html;
                            } else if (meta) {
                                var span = document.createElement('span');
                                span.className = 'chat-log__status text-error fs-xs';
                                span.setAttribute('data-msg-status', msg.msg_id);
                                span.setAttribute('title', newStatus);
                                span.innerHTML = html;
                                meta.appendChild(span);
                            }
                            patched = true;
                        } else if (newStatus === 'queued') {
                            var qhtml = '<i class="fa-solid fa-clock"></i>';
                            if (statusEl) {
                                statusEl.className = 'chat-log__status text-muted fs-xs';
                                statusEl.setAttribute('title', newStatus);
                                statusEl.innerHTML = qhtml;
                            } else if (meta) {
                                var qspan = document.createElement('span');
                                qspan.className = 'chat-log__status text-muted fs-xs';
                                qspan.setAttribute('data-msg-status', msg.msg_id);
                                qspan.setAttribute('title', newStatus);
                                qspan.innerHTML = qhtml;
                                meta.appendChild(qspan);
                            }
                            patched = true;
                        } else if (newStatus === 'sent' || newStatus === 'delivered') {
                            // Happy path: remove any existing status icon
                            if (statusEl) statusEl.remove();
                            patched = true;
                        }
                    }
                }

                if (!patched) {
                    var dqMarker = document.querySelector('[data-case-dq-id]');
                    if (dqMarker) {
                        var dqId = dqMarker.getAttribute('data-case-dq-id');
                        var timeline = document.getElementById('sheet-timeline');
                        if (timeline && window.htmx) {
                            htmx.ajax('GET', '/chatbots/collections/review/' + dqId + '/timeline/', {
                                target: '#sheet-timeline', swap: 'outerHTML'
                            });
                        }
                    }
                }
            }

            if (msg.unprocessed_counts) {
                updateUnreadBadges(msg.unprocessed_counts);
            }

            // A phone call was logged to a leasing lead — refresh its detail
            // Activity card if it's on screen (lead or inquiry detail). Distinct
            // from case_updated: no timeline/list-row/attention work.
            if (msg.type === 'call_logged') {
                refreshCallActivityCard(msg.lead_id);
            }

            if (msg.type === 'case_updated') {
                var marker, timelineUrl;
                if (msg.lead_id) {
                    marker = document.querySelector('[data-case-lead-id="' + msg.lead_id + '"]');
                    timelineUrl = '/chatbots/leasing/review/' + msg.lead_id + '/timeline/';
                } else {
                    marker = document.querySelector('[data-case-dq-id="' + msg.dq_id + '"]');
                    timelineUrl = '/chatbots/collections/review/' + msg.dq_id + '/timeline/';
                }
                if (marker) {
                    // Refresh the timeline
                    var timeline = document.getElementById('sheet-timeline');
                    if (timeline && window.htmx) {
                        htmx.ajax('GET', timelineUrl, {
                            target: '#sheet-timeline', swap: 'outerHTML'
                        });
                    }
                    // Refresh the status dropdown (collections only — leasing has no equivalent)
                    if (msg.dq_id && window.htmx) {
                        var dropdownEl = document.getElementById('case-status-dropdown');
                        if (dropdownEl) {
                            htmx.ajax('GET', '/chatbots/collections/review/' + msg.dq_id + '/status-dropdown/', {
                                target: '#case-status-dropdown', swap: 'outerHTML'
                            });
                        }
                    }
                    // Clear processing state
                    setCaseProcessing(false);
                    // Show toast (non-info; the info/new-inquiry toast is handled at the list
                    // level below so it fires even when this lead's sheet isn't open)
                    if (msg.toast && msg.kind !== 'info' && msg.kind !== 'tour' && window.SeeMe && SeeMe.toast) {
                        SeeMe.toast.success(msg.toast);
                    }
                }

                // New-inquiry FYI: fires at the list level (whether or not any sheet is open),
                // coalesced so a back-to-back burst shows one "N new inquiries" toast + one ping.
                // Stays off the draft/escalation dedupe; the per-inquiry row refresh below still runs.
                if (msg.kind === 'info') {
                    flashNewInquiry();
                }
                // Tour booked/confirmed — sticky, clickable blue notification (auto-sent, no draft).
                if (msg.kind === 'tour') {
                    flashTourEvent(msg.toast, msg.lead_id);
                }

                // Refresh the list-page row if visible (runs regardless of sheet state)
                if (msg.lead_id) {
                    refreshListRow('leasing-lead-' + msg.lead_id, '/propmgmt/leasing-leads/' + msg.lead_id + '/row/');
                    // Lead detail page under the sheet — its chip bar (Escalated, Flagged, status)
                    if (document.querySelector('[data-lead-banner="' + msg.lead_id + '"]')) {
                        refreshListRow('lead-banner', '/propmgmt/leasing-leads/' + msg.lead_id + '/banner/');
                    }
                    // Inquiry list — clear/drop the Needs-Review badge for this lead's rows
                    refreshInquiryRowsForLead(msg.lead_id);
                    // Review activity log / dashboard — refresh this lead's entry rows
                    // (flag chip counts) whether or not a case sheet is open over them.
                    refreshEntryRowsForLead(msg.lead_id);
                    // Attention counts — instant update via WS path (deduped by id Sets)
                    refreshAttention();
                }
                if (msg.dq_id) {
                    refreshListRow('delinquency-' + msg.dq_id, '/propmgmt/rentals/delinquency/' + msg.dq_id + '/row/');
                }
            }
        };

        socket.onclose = function (e) {
            socket = null;
            // Reset session-scoped read receipts — a fresh connection should
            // re-send for any bubbles still on screen.
            sentMarkAsReadIds = new Set();
            // Unauthorized — redirect to login
            if (e.code === 4401) {
                window.location.href = '/login/?next=' + encodeURIComponent(window.location.pathname);
                return;
            }
            // Don't reconnect if we intentionally disconnected (navigated away)
            if (!activeEndpoint) return;
            if (reconnectAttempts < 10) {
                var delay = Math.min(2000 * Math.pow(1.5, reconnectAttempts), 30000);
                reconnectAttempts++;
                reconnectTimer = setTimeout(function () { connect(endpoint); }, delay);
            }
        };
    }

    function disconnect() {
        if (socket) {
            socket.close();
            socket = null;
        }
        activeEndpoint = null;
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    }

    // --- Connection lifecycle ------------------------------------------------

    function checkConnection() {
        var endpoint = getActiveEndpoint();
        if (endpoint) {
            // Need to connect or switch endpoints
            if (!socket || !activeEndpoint || activeEndpoint.ws !== endpoint.ws) {
                disconnect();
                reconnectAttempts = 0;
                connect(endpoint);
            }
        } else {
            // Not on a review page — tear down
            disconnect();
        }
        // Start/stop the leasing attention poll based on list-tab marker presence
        if (getAttentionMarker()) {
            startAttentionPoll();
        } else {
            stopAttentionPoll();
        }
    }

    // Initial connection on page load
    checkConnection();

    // SPA forward navigation
    document.body.addEventListener('htmx:pushedIntoHistory', function () {
        checkConnection();
    });

    // Browser back/forward
    window.addEventListener('popstate', function () {
        checkConnection();
    });

    // --- Form handling -------------------------------------------------------

    // Handle instruction/evaluate submissions — set processing state on 204 responses
    document.body.addEventListener('htmx:afterRequest', function (e) {
        if (e.detail.xhr && e.detail.xhr.status === 204) {
            var el = e.detail.elt;
            var isForm = el.id === 'case-action-form' || el.id === 'leasing-case-action-form';
            var isEvalBtn = el.id === 'case-evaluate-btn' || el.id === 'leasing-case-evaluate-btn';
            if (isForm || isEvalBtn) {
                // Grab the case id from whichever marker is present
                var marker = document.querySelector('[data-case-dq-id], [data-case-lead-id]');
                var caseId = null;
                if (marker) {
                    caseId = marker.getAttribute('data-case-dq-id') || marker.getAttribute('data-case-lead-id');
                }
                setCaseProcessing(true, caseId);
                if (isForm) el.reset();
            }
        }
    });

    // For instruction mode, prevent HTMX from trying to swap the 204 response into the timeline
    document.body.addEventListener('htmx:beforeRequest', function (e) {
        var el = e.detail.elt;
        if (el.id === 'case-action-form' || el.id === 'leasing-case-action-form') {
            var radio = el.querySelector('input[name="send_as"]:checked')
                || document.querySelector('input[name="send_as"]:checked');
            if (radio && radio.value === 'instruction') {
                // Switch to no-swap mode for async instruction
                el.setAttribute('hx-swap', 'none');
            } else {
                // Restore normal swap for message mode
                el.setAttribute('hx-swap', 'outerHTML');
            }
            htmx.process(el);
        }
    });

    // Sheet-aware connect + restore processing state after any htmx swap
    document.body.addEventListener('htmx:afterSettle', function () {
        checkConnection();
        if (!processingDqId) return;
        var marker = document.querySelector(
            '[data-case-dq-id="' + processingDqId + '"], [data-case-lead-id="' + processingDqId + '"]'
        );
        if (marker && !document.querySelector('.is-processing')) {
            setCaseProcessing(true, processingDqId);
        }
    });

    // Scroll the open case sheet's timeline to the newest (bottom) entry.
    function scrollTimelineToBottom() {
        var t = document.getElementById('sheet-timeline');
        var body = t && t.closest('.sheet__body');
        if (body) requestAnimationFrame(function () { body.scrollTop = body.scrollHeight; });
    }

    // Mark inbound messages as read whenever the timeline (re)renders — fires
    // on initial sheet open and on every timeline refresh (case_updated, status
    // updates, message send, etc.). Filters by target so unrelated swaps don't
    // trigger needless WS sends. A full timeline refresh also snaps to the
    // newest entry; "Load older" is excluded (it targets #load-older-wrap, not
    // #sheet-timeline) so paging back keeps its scroll position.
    document.body.addEventListener('htmx:afterSwap', function (e) {
        var target = e.detail && e.detail.target;
        if (!target) return;
        if (target.id === 'sheet-timeline' || (target.querySelector && target.querySelector('#sheet-timeline'))) {
            collectAndSendMarkAsRead();
            // Skip the snap-to-bottom while paging older history in — the sheet's own
            // handler restores the prior scroll position so the view doesn't jump.
            if (!window.__collLoadingOlder) scrollTimelineToBottom();
        }
    });

    window.setCaseProcessing = setCaseProcessing;

})();
